import FingerprintJS from '@fingerprintjs/fingerprintjs';
import type { DPData, AppInfo } from './types';

// ==================== ApiError ====================

export class ApiError extends Error {
  readonly code: number;
  constructor(message: string, code: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

// ==================== client_id ====================

const CLIENT_ID_STORAGE_KEY = '__dp_sdk_client_id__';

let _clientIdPromise: Promise<string> | null = null;

export function getClientId(): Promise<string> {
  if (!_clientIdPromise) {
    _clientIdPromise = FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => result.visitorId)
      .catch(() => {
        const stored = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
        if (stored) return stored;
        const id = crypto.randomUUID();
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, id);
        return id;
      });
  }
  return _clientIdPromise;
}

// ==================== API 响应类型 ====================

export interface EnterRoomResponse {
  room_id: string;
  project_id: string;
  pod_id: number;
  rag_url: string;
  rtc_config: {
    url: string;
    room_id: string;
    user_id: string;
    token: string;
  };
}

export interface HotWordsResponse {
  asr_words: string[];
  tts_words: string[];
}

// ==================== 请求策略 ====================

interface RequestPolicyConfig {
  timeoutMs: number;
  retry: number;
  retryBackoffMs: number;
  retryJitterMs: number;
}

const DEFAULT_REQUEST_POLICY: RequestPolicyConfig = {
  timeoutMs: 10_000,
  retry: 2,
  retryBackoffMs: 1000,
  retryJitterMs: 300,
};

// ==================== ApiService ====================

export class ApiService {
  private _baseUrl: string;
  private _getToken: () => string;
  private _policy: RequestPolicyConfig;

  constructor(
    baseUrl: string,
    accessToken: string | (() => string),
    policy?: Partial<RequestPolicyConfig>,
  ) {
    this._baseUrl = baseUrl.replace(/\/+$/, '');
    this._getToken = typeof accessToken === 'function' ? accessToken : () => accessToken;
    this._policy = { ...DEFAULT_REQUEST_POLICY, ...policy };
  }

  private async _request<T>(url: string, options?: RequestInit): Promise<T> {
    const token = this._getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options?.headers as Record<string, string>) || {}),
    };
    if (token) {
      headers['access_token'] = token;
    }

    let lastError: Error | null = null;
    const maxAttempts = 1 + this._policy.retry;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        const backoff = this._policy.retryBackoffMs * Math.pow(2, attempt - 1);
        const jitter = Math.random() * this._policy.retryJitterMs;
        await new Promise((r) => setTimeout(r, backoff + jitter));
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this._policy.timeoutMs);

        const resp = await fetch(`${this._baseUrl}${url}`, {
          ...options,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
        }

        const json = await resp.json();
        if (json.code !== 0 && json.code !== 200) {
          throw new ApiError(
            json.msg || json.err_message || `API error (code: ${json.code})`,
            json.code,
          );
        }
        return json;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt === maxAttempts - 1) break;
      }
    }

    throw lastError!;
  }

  async getTaskDetail(taskId: string): Promise<DPData> {
    const res = await this._request<{ data: DPData }>(
      `/sdk/task/detail?task_id=${encodeURIComponent(taskId)}`,
      { method: 'GET' },
    );
    return res.data;
  }

  async getAppInfo(): Promise<AppInfo> {
    const res = await this._request<{ data: AppInfo }>('/sdk/app/info', {
      method: 'GET',
    });
    return res.data;
  }

  async enterRoom(taskId: string): Promise<EnterRoomResponse> {
    const clientId = await getClientId();
    const res = await this._request<{ data: EnterRoomResponse }>('/sdk/room/enter', {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId, client_id: clientId }),
    });
    return res.data;
  }

  async exitRoom(roomId: string): Promise<void> {
    const clientId = await getClientId();
    await this._request('/sdk/room/exit', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, room_id: roomId }),
    });
  }

  async getHotWords(): Promise<HotWordsResponse> {
    try {
      const res = await this._request<{ data: HotWordsResponse }>('/sdk/hot_word/get_all', {
        method: 'GET',
      });
      return res.data;
    } catch {
      return { asr_words: [], tts_words: [] };
    }
  }
}
